import React, { useEffect, useRef, useState } from "react"
import { useStaticQuery, graphql, withPrefix } from "gatsby"
import { MapContainer, TileLayer, Marker } from "react-leaflet"
import { DivIcon } from "leaflet"
import Link from "~components/link/link"
import ToolTip from "~components/tooltip/tooltip"
import * as styles from "./fastly-info-map.module.css"

const SHUNT_X_PC = 35
const SHUNT_Y_PC = -30
const DEG_PER_PX = 0.035
const SERVER_TIMING_TEST_VALUE = { pop: "CHI", cache: "MISS", fetchDur: 4 }
const CACHE_STATE_LABEL_MISS = "Origin fetch"
const CACHE_STATE_LABEL_HIT = "Cache hit"
const TIPS = {
  [CACHE_STATE_LABEL_MISS]: "Fastly fetched this resource from origin and cached it at the edge for future requests",
  [CACHE_STATE_LABEL_HIT]: "This resource was available in the local Fastly edge cache",
}
const LOCAL_GREETING = {
  ADL: "G'Day",
  BNE: "G'Day",
  SYD: "G'Day",
  MEL: "G'Day",
  FRA: "Hallo",
  HHN: "Hallo",
  WIE: "Hallo",
  LIS: "Oi",
  MAD: "Hola",
  CDG: "Salut",
  TOK: "やあ",
  HND: "やあ",
  NRT: "やあ",
  HKG: "你好",
}

const getServerTiming = (nt) => {
  if (window.location.hostname === "localhost") return SERVER_TIMING_TEST_VALUE
  if (!nt || !nt.serverTiming) return null
  const raw = nt.serverTiming.find((x) => x.name === "fastly")
  if (!raw) return null
  const data = raw.description.split(":")
  return { pop: data[0], cache: data[1], fetchDur: data[2] }
}

const gql = graphql`
  query {
    pops: allPopsJson(sort: { fields: name }) {
      nodes {
        popCode
        name
        pnis
        shieldCode
        recommend
        hostCount
        geo {
          lat
          lng
        }
      }
    }
  }
`

const mapIcon = new DivIcon({
  className: styles.mapIcon,
  iconSize: [100, 100],
  iconAnchor: [50, 50],
  html: `<embed src=${withPrefix(`/pop-map-marker.svg`)}>`,
})

const FastlyInfoMap = () => {
  const [shunts, setShunts] = useState([0, 0])
  const container = useRef(null)
  const POPS = useStaticQuery(gql).pops.nodes

  useEffect(() => {
    if (container.current) {
      setShunts([
        container.current.offsetWidth * (SHUNT_X_PC / 100) * DEG_PER_PX,
        container.current.offsetHeight * (SHUNT_Y_PC / 100) * DEG_PER_PX,
      ])
    }
  }, [])

  const nt = window.performance.getEntriesByType("navigation")[0]

  if (!nt || nt.name.replace(/https?:\/\/.*?\//, "/") !== window.location.pathname) return null

  const st = getServerTiming(nt)
  if (!st) return null
  const thisPOP = POPS.find((p) => p.popCode === st.pop)
  if (!thisPOP) return null

  const perfMeasures = {
    dns: Math.ceil(nt.domainLookupEnd - nt.domainLookupStart),
    connect: Math.ceil(nt.connectEnd - nt.connectStart),
    request: Math.ceil(nt.responseStart - nt.requestStart),
    response: Math.ceil(nt.responseEnd - nt.responseStart),
  }
  const totalRTT = Object.values(perfMeasures).reduce((sum, x) => sum + x, 0)
  const protocol = (nt.nextHopProtocol || "").toUpperCase()
  const cacheDesc = st.cache ? (st.cache.includes("HIT") ? CACHE_STATE_LABEL_HIT : CACHE_STATE_LABEL_MISS) : undefined
  const originFetchDur = st.fetchDur

  const position = thisPOP && [thisPOP.geo.lat, thisPOP.geo.lng]

  const shuntedPosition = thisPOP && [thisPOP.geo.lat + shunts[1], thisPOP.geo.lng + shunts[0]]

  return (
    <div className={styles.map} ref={container}>
      {Boolean(shunts[0]) && (
        <>
          <MapContainer
            center={shuntedPosition}
            zoom={5}
            scrollWheelZoom={false}
            attributionControl={false}
            zoomControl={false}
          >
            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
            <Marker position={position} icon={mapIcon} />
          </MapContainer>

          <div className={styles.overlay}>
            <div className={styles.info}>
              <div>
                <ol className={styles.tags}>
                  {Boolean(protocol) && <li data-infomaptip={TIPS[protocol]}>{protocol}</li>}
                  {Boolean(cacheDesc) && <li data-infomaptip={TIPS[cacheDesc]}>{cacheDesc}</li>}
                  <li data-infomaptip="Total network time for this request">{totalRTT} ms</li>
                </ol>
                {LOCAL_GREETING[thisPOP.popCode] || "Hi"} from Fastly in{" "}
                <span className={styles.popName}>{thisPOP.name}</span>!
              </div>
              <div className={styles.timeline}>
                <div
                  className={styles.timeDns}
                  data-infomaptip={`DNS lookup: ${perfMeasures.dns}ms`}
                  style={{ width: `${Math.ceil((perfMeasures.dns / totalRTT) * 100)}%` }}
                />
                <div
                  className={styles.timeConnect}
                  data-infomaptip={`Connect: ${perfMeasures.connect}ms`}
                  style={{ width: `${Math.ceil((perfMeasures.connect / totalRTT) * 100)}%` }}
                />
                <div
                  className={styles.timeRequest}
                  data-infomaptip={`Request to Fastly (TTFB): ${perfMeasures.request}ms`}
                  style={{ width: `${Math.ceil((perfMeasures.request / totalRTT) * 100)}%` }}
                >
                  {Boolean(cacheDesc !== CACHE_STATE_LABEL_HIT && originFetchDur) && (
                    <div
                      className={styles.timeOriginFetch}
                      data-infomaptip={`Fastly to origin (TTFB): ${originFetchDur}ms`}
                      style={{ width: `${Math.ceil((originFetchDur / totalRTT) * 100)}%` }}
                    />
                  )}
                </div>
                <div
                  className={styles.timeResponse}
                  data-infomaptip={`Download response: ${perfMeasures.response}ms`}
                  style={{ width: `${Math.ceil((perfMeasures.response / totalRTT) * 100)}%` }}
                />
              </div>

              <div className={styles.attribution}>
                Map from our friends at <Link to="https://openstreetmap.org">OpenStreetMap</Link> and contributors.
                Rendered by <Link to="https://leafletjs.com">🇺🇦 Leaflet</Link>.
              </div>
              {/*<div className={styles.ffpromo}>
                OpenStreetMap is a member of our{" "}
                <Link to="https://www.fastly.com/forward">
                  <img src="/fastforward-logo.svg" alt="Fast Forward logo" width={100} className={styles.fflogo} />
                </Link>{" "}
                community program, which nurtures and supports projects that share our vision of an open internet that is
                fast and secure for all.
              </div>*/}
            </div>
          </div>
          <ToolTip target="[data-infomaptip]" contentAttr="data-infomaptip" trigger="hover" type="light" />
        </>
      )}
    </div>
  )
}

export default FastlyInfoMap
